import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FiPlus, FiSettings, FiGrid, FiList, FiTrash2, FiEdit2, FiEye, FiEyeOff, FiServer, FiCpu, FiHardDrive, FiActivity, FiKey, FiLock } from 'react-icons/fi';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Header from '../header/header';
import SideBar from '../sidebar/sideBar';

const API_URL = process.env.REACT_APP_API_URL;

interface ServiceType {
  id: number;
  name: string;
  has_hosting_details: number;
  hsn_code?: string;
  price?: number | string;
  description?: string;
}

interface ServiceItem {
  id: number;
  customer_id: number | null;
  customer_name: string | null;
  service_type_id: number;
  service_type_name: string;
  has_hosting_details: number;
  name: string;
  price: number | string;
  billing_cycle: string;
  status: string;
  start_date: string | null;
  expiry_date: string | null;
  server_name: string | null;
  ip_address: string | null;
  cpu: string | null;
  ram: string | null;
  hd: string | null;
  backup: string | null;
  panel: string | null;
  username: string | null;
  password: string | null;
  remarks: string | null;
  description?: string | null;
}

interface Customer {
  id: number;
  company_name: string;
  email?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  gst_number?: string;
  pancard?: string;
  kyc_document_url?: string;
}

function parseHd(hdStr: string | undefined | null) {
  if (!hdStr) return { size: "", type: "SSD" };
  const match = hdStr.match(/^([\d.]+)\s*(?:GB)?\s*(SSD|NVMe|NVME|HDD|HD)/i);
  if (match) {
    let type = match[2].toUpperCase();
    if (type === "HD") type = "HDD";
    return { size: match[1], type };
  }
  return { size: hdStr.replace(/\s*(GB|SSD|NVMe|NVME|HDD|HD)/gi, ""), type: "SSD" };
}

const getSubdomain = () => {
  const host = window.location.host;
  const parts = host.split('.');
  if (host.includes('localhost') || host.includes('127.0.0.1')) {
    if (parts.length > 1 && parts[0] !== 'localhost') {
      return parts[0];
    }
  } else {
    if (parts.length > 2) {
      return parts[0];
    }
  }
  return null;
};

const Services: React.FC = () => {
  const [services, setServices] = useState<ServiceItem[]>([]);
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [subdomain, setSubdomain] = useState<string | null>(null);
  const [matchedCompany, setMatchedCompany] = useState<any>(null);
  const [isPublicView, setIsPublicView] = useState(false);

  // Modals state
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [isTypeModalOpen, setIsTypeModalOpen] = useState(false);
  const [editingService, setEditingService] = useState<ServiceItem | null>(null);
  const [editingType, setEditingType] = useState<ServiceType | null>(null);

  // Password visibility map
  const [visiblePasswords, setVisiblePasswords] = useState<{ [key: number]: boolean }>({});

  // Local Specs Form States
  const [ramSize, setRamSize] = useState('');
  const [hdSize, setHdSize] = useState('');
  const [hdType, setHdType] = useState('SSD');

  // Order & KYC States
  const [companies, setCompanies] = useState<any[]>([]);
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [selectedOrderServiceType, setSelectedOrderServiceType] = useState<ServiceType | null>(null);
  const [useExistingCustomer, setUseExistingCustomer] = useState(true);
  const [kycFile, setKycFile] = useState<File | null>(null);
  const [uploadingKyc, setUploadingKyc] = useState(false);
  const [orderForm, setOrderForm] = useState({
    company_name: '',
    email: '',
    address: '',
    city: '',
    state: '',
    country: 'India',
    gst_number: '',
    pancard: '',
    password: '',
    customer_id: '',
    company_id: '',
    billing_cycle: 'One-Time'
  });
  
  // Masters lists states
  const [controlPanels, setControlPanels] = useState<any[]>([]);
  const [backupProviders, setBackupProviders] = useState<any[]>([]);
  const [activeConfigTab, setActiveConfigTab] = useState<'service_types' | 'control_panels' | 'backup_providers'>('service_types');
  
  // Masters edit forms states
  const [panelForm, setPanelForm] = useState({ name: '' });
  const [backupForm, setBackupForm] = useState({ name: '' });
  const [editingPanel, setEditingPanel] = useState<any | null>(null);
  const [editingBackup, setEditingBackup] = useState<any | null>(null);

  // Form states for Services
  const [serviceForm, setServiceForm] = useState({
    customer_id: '',
    service_type_id: '',
    name: '',
    price: '',
    billing_cycle: 'One-Time',
    status: 'Active',
    start_date: '',
    expiry_date: '',
    server_name: '',
    ip_address: '',
    cpu: '',
    ram: '',
    hd: '',
    backup: '',
    panel: '',
    username: '',
    password: '',
    remarks: '',
    description: ''
  });

  // Form states for Service Type Masters
  const [typeForm, setTypeForm] = useState({
    name: '',
    has_hosting_details: false,
    hsn_code: '',
    price: '',
    description: ''
  });

  // Fetch initial data
  const fetchData = async () => {
    setLoading(true);
    setError('');

    const urlParams = new URLSearchParams(window.location.search);
    const querySub = urlParams.get('subdomain') || urlParams.get('company_code');
    const hostSub = getSubdomain();
    const finalSub = hostSub || querySub;
    const token = localStorage.getItem('token');
    const publicMode = !token || finalSub !== null;

    try {
      if (publicMode) {
        // Public Catalog Mode
        const res = await axios.get(`${API_URL}/auth/public/services?subdomain=${finalSub || ''}`);
        const { company, serviceTypes: publicTypes } = res.data || {};
        setServiceTypes(Array.isArray(publicTypes) ? publicTypes : []);
        setMatchedCompany(company);
        
        if (!company) {
          const companiesRes = await axios.get(`${API_URL}/auth/companies`);
          setCompanies(Array.isArray(companiesRes.data) ? companiesRes.data : []);
        } else {
          setCompanies([company]);
        }
      } else {
        // Admin Mode
        const [servicesRes, typesRes, customersRes, panelsRes, backupsRes, companiesRes] = await Promise.all([
          axios.get(`${API_URL}/auth/services`),
          axios.get(`${API_URL}/auth/service-types`),
          axios.get(`${API_URL}/auth/customers`),
          axios.get(`${API_URL}/auth/control-panels`),
          axios.get(`${API_URL}/auth/backup-providers`),
          axios.get(`${API_URL}/auth/companies`)
        ]);
        setServices(Array.isArray(servicesRes.data) ? servicesRes.data : []);
        setServiceTypes(Array.isArray(typesRes.data) ? typesRes.data : []);
        setCustomers(Array.isArray(customersRes.data) ? customersRes.data : []);
        setControlPanels(Array.isArray(panelsRes.data) ? panelsRes.data : []);
        setBackupProviders(Array.isArray(backupsRes.data) ? backupsRes.data : []);
        setCompanies(Array.isArray(companiesRes.data) ? companiesRes.data : []);
      }
    } catch (err) {
      setError('Error loading services metadata');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const querySub = urlParams.get('subdomain') || urlParams.get('company_code');
    const hostSub = getSubdomain();
    const finalSub = hostSub || querySub;
    setSubdomain(finalSub);

    const token = localStorage.getItem('token');
    const publicMode = !token || finalSub !== null;
    setIsPublicView(publicMode);

    fetchData();
  }, []);

  const handleOpenOrderModal = (serviceType: ServiceType) => {
    setSelectedOrderServiceType(serviceType);
    
    const token = localStorage.getItem('token');
    const urlParams = new URLSearchParams(window.location.search);
    const querySub = urlParams.get('subdomain') || urlParams.get('company_code');
    const hostSub = getSubdomain();
    const finalSub = hostSub || querySub;
    const publicMode = !token || finalSub !== null;

    setUseExistingCustomer(!publicMode);
    setKycFile(null);

    const compId = matchedCompany ? String(matchedCompany.id) : (companies.length === 1 ? String(companies[0].id) : (companies.length > 0 ? String(companies[0].id) : ''));

    setOrderForm({
      company_name: '',
      email: '',
      address: '',
      city: '',
      state: '',
      country: 'India',
      gst_number: '',
      pancard: '',
      password: '',
      customer_id: '',
      company_id: compId,
      billing_cycle: 'One-Time'
    });
    setIsOrderModalOpen(true);
  };

  const handlePlaceOrder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedOrderServiceType) return;
    if (!orderForm.company_id) {
      alert("Seller Company is required.");
      return;
    }

    try {
      setUploadingKyc(true);
      
      // 1. Upload KYC Document if exists
      let uploadedUrl = '';
      if (kycFile) {
        const formData = new FormData();
        formData.append('file', kycFile);
        const uploadRes = await axios.post(`${API_URL}/auth/upload-attachment`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        if (uploadRes.data && uploadRes.data.file_url) {
          uploadedUrl = uploadRes.data.file_url;
        }
      }

      // 2. Build Payload
      let payload: any = {};
      if (useExistingCustomer) {
        if (!orderForm.customer_id) {
          alert("Please select a customer.");
          setUploadingKyc(false);
          return;
        }
        const cust = customers.find(c => String(c.id) === String(orderForm.customer_id));
        if (!cust) {
          alert("Selected customer not found.");
          setUploadingKyc(false);
          return;
        }
        payload = {
          company_name: cust.company_name,
          email: cust.email,
          address: cust.address,
          city: cust.city,
          state: cust.state,
          country: cust.country,
          gst_number: cust.gst_number,
          pancard: orderForm.pancard || cust.pancard,
          company_id: Number(orderForm.company_id),
          service_type_id: selectedOrderServiceType.id,
          billing_cycle: orderForm.billing_cycle,
          kyc_document_url: uploadedUrl || cust.kyc_document_url
        };
      } else {
        if (!orderForm.company_name || !orderForm.email) {
          alert("Company name and email are required for new customer.");
          setUploadingKyc(false);
          return;
        }
        payload = {
          company_name: orderForm.company_name,
          email: orderForm.email,
          address: orderForm.address,
          city: orderForm.city,
          state: orderForm.state,
          country: orderForm.country,
          gst_number: orderForm.gst_number,
          pancard: orderForm.pancard,
          password: orderForm.password || undefined,
          company_id: Number(orderForm.company_id),
          service_type_id: selectedOrderServiceType.id,
          billing_cycle: orderForm.billing_cycle,
          kyc_document_url: uploadedUrl
        };
      }

      // 3. Purchase Service Call
      const purchaseRes = await axios.post(`${API_URL}/auth/services/purchase`, payload);
      alert(`Service ordered successfully! Invoice generated: ${purchaseRes.data.invoiceNo}`);
      setIsOrderModalOpen(false);
      fetchData(); // Refresh list to show new service
    } catch (err: any) {
      alert(err.response?.data?.error || "Failed to order service.");
    } finally {
      setUploadingKyc(false);
    }
  };

  // Open modal to add service
  const handleAddServiceClick = () => {
    setEditingService(null);
    setRamSize('');
    setHdSize('');
    setHdType('SSD');
    setServiceForm({
      customer_id: '',
      service_type_id: '',
      name: '',
      price: '',
      billing_cycle: 'One-Time',
      status: 'Active',
      start_date: '',
      expiry_date: '',
      server_name: '',
      ip_address: '',
      cpu: '',
      ram: '',
      hd: '',
      backup: '',
      panel: '',
      username: '',
      password: '',
      remarks: '',
      description: ''
    });
    setIsServiceModalOpen(true);
  };

  // Open modal to edit service
  const handleEditServiceClick = (service: ServiceItem) => {
    setEditingService(service);
    
    // Parse specs for form edit
    const parsedRam = service.ram ? service.ram.replace(/\s*GB/gi, '') : '';
    const parsedHd = parseHd(service.hd);
    setRamSize(parsedRam);
    setHdSize(parsedHd.size);
    setHdType(parsedHd.type);

    setServiceForm({
      customer_id: service.customer_id ? String(service.customer_id) : '',
      service_type_id: String(service.service_type_id),
      name: service.name,
      price: String(service.price),
      billing_cycle: service.billing_cycle,
      status: service.status,
      start_date: service.start_date ? service.start_date.substring(0, 10) : '',
      expiry_date: service.expiry_date ? service.expiry_date.substring(0, 10) : '',
      server_name: service.server_name || '',
      ip_address: service.ip_address || '',
      cpu: service.cpu || '',
      ram: service.ram || '',
      hd: service.hd || '',
      backup: service.backup || '',
      panel: service.panel || '',
      username: service.username || '',
      password: service.password || '',
      remarks: service.remarks || '',
      description: service.description || ''
    });
    setIsServiceModalOpen(true);
  };

  // Delete service
  const handleDeleteService = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    try {
      await axios.delete(`${API_URL}/auth/services/${id}`);
      fetchData();
    } catch (err) {
      alert('Failed to delete service');
    }
  };

  // Save Service (Create or Update)
  const handleSaveService = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!serviceForm.service_type_id || !serviceForm.name) {
      alert('Please enter a service name and select a service type.');
      return;
    }
    const payload = {
      ...serviceForm,
      customer_id: serviceForm.customer_id ? Number(serviceForm.customer_id) : null,
      service_type_id: Number(serviceForm.service_type_id),
      price: serviceForm.price ? Number(serviceForm.price) : 0,
      start_date: serviceForm.start_date || null,
      expiry_date: serviceForm.expiry_date || null,
      ram: ramSize ? `${ramSize} GB` : '',
      hd: hdSize ? `${hdSize} GB ${hdType}` : '',
    };

    try {
      if (editingService) {
        await axios.put(`${API_URL}/auth/services/${editingService.id}`, payload);
      } else {
        await axios.post(`${API_URL}/auth/services`, payload);
      }
      setIsServiceModalOpen(false);
      fetchData();
    } catch (err) {
      alert('Error saving service');
    }
  };

  // Save Control Panel (Master)
  const handleSavePanel = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!panelForm.name.trim()) return;
    const payload = { name: panelForm.name.trim() };
    try {
      if (editingPanel) {
        await axios.put(`${API_URL}/auth/control-panels/${editingPanel.id}`, payload);
      } else {
        await axios.post(`${API_URL}/auth/control-panels`, payload);
      }
      setPanelForm({ name: '' });
      setEditingPanel(null);
      fetchData();
    } catch (err) {
      alert('Error saving control panel');
    }
  };

  const handleEditPanelClick = (panel: any) => {
    setEditingPanel(panel);
    setPanelForm({ name: panel.name });
  };

  const handleDeletePanel = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this control panel master?')) return;
    try {
      await axios.delete(`${API_URL}/auth/control-panels/${id}`);
      fetchData();
    } catch (err) {
      alert('Failed to delete control panel');
    }
  };

  // Save Backup Provider (Master)
  const handleSaveBackup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!backupForm.name.trim()) return;
    const payload = { name: backupForm.name.trim() };
    try {
      if (editingBackup) {
        await axios.put(`${API_URL}/auth/backup-providers/${editingBackup.id}`, payload);
      } else {
        await axios.post(`${API_URL}/auth/backup-providers`, payload);
      }
      setBackupForm({ name: '' });
      setEditingBackup(null);
      fetchData();
    } catch (err) {
      alert('Error saving backup provider');
    }
  };

  const handleEditBackupClick = (backup: any) => {
    setEditingBackup(backup);
    setBackupForm({ name: backup.name });
  };

  const handleDeleteBackup = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this backup provider master?')) return;
    try {
      await axios.delete(`${API_URL}/auth/backup-providers/${id}`);
      fetchData();
    } catch (err) {
      alert('Failed to delete backup provider');
    }
  };

  // Save Service Type (Master)
  const handleSaveType = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!typeForm.name.trim()) return;
    const payload = {
      name: typeForm.name,
      has_hosting_details: typeForm.has_hosting_details ? 1 : 0,
      hsn_code: typeForm.hsn_code.trim() || null,
      price: typeForm.price ? Number(typeForm.price) : 0,
      description: typeForm.description
    };
    try {
      if (editingType) {
        await axios.put(`${API_URL}/auth/service-types/${editingType.id}`, payload);
      } else {
        await axios.post(`${API_URL}/auth/service-types`, payload);
      }
      setTypeForm({ name: '', has_hosting_details: false, hsn_code: '', price: '', description: '' });
      setEditingType(null);
      fetchData();
    } catch (err) {
      alert('Error saving service type');
    }
  };

  const handleEditTypeClick = (type: ServiceType) => {
    setEditingType(type);
    setTypeForm({
      name: type.name,
      has_hosting_details: type.has_hosting_details === 1,
      hsn_code: type.hsn_code || '',
      price: type.price ? String(type.price) : '',
      description: type.description || ''
    });
  };

  const handleDeleteType = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this service type master? All services using this type will be affected.')) return;
    try {
      await axios.delete(`${API_URL}/auth/service-types/${id}`);
      fetchData();
    } catch (err) {
      alert('Failed to delete service type');
    }
  };

  const togglePasswordVisibility = (id: number) => {
    setVisiblePasswords(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Find if selected type in form has hosting details
  const selectedType = serviceTypes.find(t => String(t.id) === serviceForm.service_type_id);
  const showHostingFields = selectedType?.has_hosting_details === 1;

  const renderMainContent = () => {
    if (isPublicView) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-violet-50/30 to-indigo-50/20 text-slate-800 font-sans pb-16 w-full">
          {/* Public Header / Navbar */}
          <div className="bg-white/70 backdrop-blur-md sticky top-0 z-40 border-b border-slate-100 px-6 py-4 flex items-center justify-between shadow-sm">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-tr from-violet-600 to-indigo-600 text-white p-2.5 rounded-2xl shadow-md">
                <FiServer className="text-xl" />
              </div>
              <div>
                <h1 className="text-lg font-black tracking-tight text-slate-900 leading-none">
                  {matchedCompany ? matchedCompany.company_name : "Services Portal"}
                </h1>
                {matchedCompany && (
                  <p className="text-[10px] text-gray-500 font-bold uppercase mt-1 tracking-wider">
                    Official Service Catalog
                  </p>
                )}
              </div>
            </div>
            <div>
              <a
                href="/login"
                className="text-xs font-bold text-violet-700 bg-violet-50 hover:bg-violet-100 px-4 py-2 rounded-xl transition"
              >
                Sign In
              </a>
            </div>
          </div>

          {/* Hero Section */}
          <div className="max-w-6xl mx-auto px-6 py-12 text-center">
            <h2 className="text-4xl md:text-5xl font-black text-slate-900 tracking-tight leading-tight">
              Our Services & Solutions
            </h2>
            <p className="text-gray-500 max-w-xl mx-auto text-sm mt-3.5 leading-relaxed">
              Select a service type below to view details and place an order. Auto-generate your digital invoice instantly.
            </p>
          </div>

          {/* Error message */}
          {error && (
            <div className="max-w-6xl mx-auto px-6 mb-6">
              <div className="p-4 bg-red-50 border border-red-200 text-red-600 rounded-2xl text-xs font-semibold">
                {error}
              </div>
            </div>
          )}

          {/* Pricing Cards Grid */}
          <div className="max-w-6xl mx-auto px-6">
            {loading ? (
              <div className="flex justify-center items-center py-20">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-violet-700"></div>
              </div>
            ) : serviceTypes.length === 0 ? (
              <div className="bg-white border border-slate-100 rounded-3xl p-12 text-center max-w-md mx-auto shadow-sm">
                <FiServer className="mx-auto text-4xl text-slate-300 mb-3" />
                <h3 className="text-base font-bold text-slate-800">No Services Available</h3>
                <p className="text-gray-400 text-xs mt-1">This company has not registered any public service types yet.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {serviceTypes.map((t) => (
                  <div key={t.id} className="bg-white border border-slate-100 rounded-3xl p-6 shadow-sm hover:shadow-md transition duration-250 flex flex-col justify-between relative group hover:border-violet-200">
                    <div>
                      <h3 className="text-xl font-black text-slate-900 tracking-tight group-hover:text-violet-700 transition">
                        {t.name}
                      </h3>
                      
                      {t.hsn_code && (
                        <span className="inline-block mt-1.5 px-2 py-0.5 bg-slate-100 text-slate-600 text-[9px] font-bold rounded uppercase tracking-wider">
                          HSN: {t.hsn_code}
                        </span>
                      )}

                      {t.description ? (
                        <div 
                          className="text-xs text-gray-500 mt-4 leading-relaxed line-clamp-4 prose prose-sm prose-slate"
                          dangerouslySetInnerHTML={{ __html: t.description }}
                        />
                      ) : (
                        <p className="text-xs text-gray-400 mt-4 italic">No description provided.</p>
                      )}
                    </div>

                    <div className="mt-8 border-t border-slate-50 pt-5">
                      <div className="flex items-baseline justify-between mb-4.5">
                        <div>
                          <span className="text-[10px] text-gray-400 font-bold block uppercase tracking-wider">Pricing</span>
                          <span className="text-2xl font-black text-slate-900">
                            ₹{Number(t.price || 0).toLocaleString('en-IN')}
                          </span>
                        </div>
                        <span className="text-xs text-gray-400 font-semibold">
                          Starting Price
                        </span>
                      </div>

                      <button
                        onClick={() => handleOpenOrderModal(t)}
                        className="w-full py-3 px-4 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white rounded-2xl text-xs font-bold shadow-md hover:shadow-lg transition flex items-center justify-center gap-2"
                      >
                        🛒 Order Service
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="p-6 min-h-screen bg-slate-50/50">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 pb-4 border-b border-violet-100">
          <div>
            <h2 className="text-3xl font-extrabold text-[#4c1d95] tracking-tight">IT & Software Services</h2>
            <p className="text-gray-500 text-sm mt-1">Manage software projects, subscriptions, hosting setups, and server specifications.</p>
          </div>
          <div className="flex gap-3 mt-4 md:mt-0">
            <button
              onClick={() => setIsTypeModalOpen(true)}
              className="flex items-center gap-2 px-4 py-2 border-2 border-violet-200 text-violet-700 bg-white hover:bg-violet-50 rounded-xl text-sm font-semibold transition"
            >
              <FiSettings />
              Manage Masters
            </button>
            <button
              onClick={handleAddServiceClick}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white rounded-xl text-sm font-semibold shadow transition"
            >
              <FiPlus />
              Add Service
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-600 rounded-xl text-sm font-medium">
            {error}
          </div>
        )}

        {/* Main Grid View */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-violet-700"></div>
          </div>
        ) : services.length === 0 ? (
          <div className="bg-white border-2 border-dashed border-violet-100 rounded-3xl p-12 text-center max-w-xl mx-auto shadow-sm">
            <FiServer className="mx-auto text-5xl text-violet-200 mb-4 animate-bounce" />
            <h3 className="text-lg font-bold text-violet-950">No Services Found</h3>
            <p className="text-gray-500 text-sm mt-2">Get started by creating your service masters, then map individual services to your active customers.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {services.map((item) => (
              <div key={item.id} className="bg-white border border-slate-100 rounded-2xl p-6 shadow-md hover:shadow-xl transition-all duration-200 flex flex-col justify-between">
                <div>
                  <div className="flex items-start justify-between">
                    <div>
                      <span className="px-2.5 py-0.5 bg-violet-100 text-violet-800 text-[10px] uppercase font-bold rounded-full">
                        {item.service_type_name}
                      </span>
                      <h3 className="text-lg font-bold text-slate-800 mt-2">{item.name}</h3>
                      <p className="text-xs text-gray-400 mt-0.5">
                        Client: <span className="font-semibold text-slate-600">{item.customer_name || 'Global'}</span>
                      </p>
                    </div>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold uppercase ${
                      item.status === 'Active' ? 'bg-green-100 text-green-800' :
                      item.status === 'Suspended' ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {item.status}
                    </span>
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-4 bg-slate-50/50 rounded-xl p-3 border border-slate-100">
                    <div>
                      <span className="text-[10px] uppercase text-gray-400 font-bold block">Pricing & Billing</span>
                      <span className="text-sm font-bold text-slate-700">₹{Number(item.price).toLocaleString('en-IN')} / {item.billing_cycle}</span>
                    </div>
                    <div>
                      <span className="text-[10px] uppercase text-gray-400 font-bold block">Expiry Date</span>
                      <span className="text-sm font-bold text-slate-700">{item.expiry_date ? new Date(item.expiry_date).toLocaleDateString('en-IN') : 'Ongoing'}</span>
                    </div>
                  </div>

                  {item.has_hosting_details === 1 && (
                    <div className="mt-5 border-t border-dashed border-slate-100 pt-4">
                      <h4 className="text-xs font-extrabold text-violet-900 uppercase tracking-wider mb-3 flex items-center gap-1">
                        <FiServer /> Server Configuration
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs text-slate-600">
                        {item.server_name && (
                          <div className="flex items-center gap-1.5">
                            <FiServer className="text-slate-400 flex-shrink-0" />
                            <span className="truncate" title={item.server_name}>Host: <strong>{item.server_name}</strong></span>
                          </div>
                        )}
                        {item.ip_address && (
                          <div className="flex items-center gap-1.5">
                            <FiActivity className="text-slate-400 flex-shrink-0" />
                            <span>IP: <strong>{item.ip_address}</strong></span>
                          </div>
                        )}
                        {item.cpu && (
                          <div className="flex items-center gap-1.5">
                            <FiCpu className="text-slate-400 flex-shrink-0" />
                            <span>CPU: <strong>{item.cpu}</strong></span>
                          </div>
                        )}
                        {item.ram && (
                          <div className="flex items-center gap-1.5">
                            <FiHardDrive className="text-slate-400 flex-shrink-0" />
                            <span>RAM: <strong>{item.ram}</strong></span>
                          </div>
                        )}
                        {item.hd && (
                          <div className="flex items-center gap-1.5">
                            <FiHardDrive className="text-slate-400 flex-shrink-0" />
                            <span>Storage: <strong>{item.hd}</strong></span>
                          </div>
                        )}
                        {item.panel && (
                          <div className="flex items-center gap-1.5">
                            <FiSettings className="text-slate-400 flex-shrink-0" />
                            <span>Panel: <strong>{item.panel}</strong></span>
                          </div>
                        )}
                      </div>

                      {/* Server Credentials */}
                      {(item.username || item.password) && (
                        <div className="mt-3 flex flex-wrap items-center gap-4 bg-violet-50/40 border border-violet-100 rounded-xl px-3 py-2 text-xs">
                          {item.username && (
                            <div>
                              <span className="text-gray-400 font-medium">User: </span>
                              <span className="font-bold text-violet-950">{item.username}</span>
                            </div>
                          )}
                          {item.password && (
                            <div className="flex items-center gap-1">
                              <span className="text-gray-400 font-medium">Pass: </span>
                              <span className="font-bold text-violet-950">
                                {visiblePasswords[item.id] ? item.password : '••••••••'}
                              </span>
                              <button
                                onClick={() => togglePasswordVisibility(item.id)}
                                className="text-violet-600 hover:text-violet-800 p-0.5 ml-0.5"
                              >
                                {visiblePasswords[item.id] ? <FiEyeOff size={13} /> : <FiEye size={13} />}
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {item.remarks && (
                    <p className="mt-3 text-xs text-gray-500 italic bg-slate-50/50 p-2 rounded-lg border border-slate-100/60">
                      Remarks: {item.remarks}
                    </p>
                  )}
                </div>

                <div className="mt-6 border-t border-slate-100 pt-4 flex justify-end gap-2">
                  <button
                    onClick={() => handleEditServiceClick(item)}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs text-indigo-700 bg-indigo-50 hover:bg-indigo-100 font-bold rounded-lg transition"
                  >
                    <FiEdit2 size={12} /> Edit
                  </button>
                  <button
                    onClick={() => handleDeleteService(item.id)}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs text-red-700 bg-red-50 hover:bg-red-100 font-bold rounded-lg transition"
                  >
                    <FiTrash2 size={12} /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {isPublicView ? (
        renderMainContent()
      ) : (
        <div className="flex flex-col h-screen overflow-hidden">
          <Header />
          <div className="flex flex-1 overflow-hidden bg-[#F7F7FB]">
            <SideBar />
            <main className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden">
              {renderMainContent()}
            </main>
          </div>
        </div>
      )}

      {/* Services Add/Edit Modal */}
      {isServiceModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl border border-slate-100">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-gradient-to-r from-violet-50/50 to-indigo-50/50">
              <h3 className="text-xl font-black text-slate-800">
                {editingService ? 'Edit Service Specification' : 'Add New Service Instance'}
              </h3>
              <button
                onClick={() => setIsServiceModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 text-xl font-bold"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleSaveService} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Customer / Client</label>
                  <select
                    value={serviceForm.customer_id}
                    onChange={e => setServiceForm(prev => ({ ...prev, customer_id: e.target.value }))}
                    className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white"
                  >
                    <option value="">Global / No Customer Mapping</option>
                    {customers.map(c => (
                      <option key={c.id} value={c.id}>{c.company_name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Service Type *</label>
                  <select
                    value={serviceForm.service_type_id}
                    onChange={e => setServiceForm(prev => ({ ...prev, service_type_id: e.target.value }))}
                    className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white"
                    required
                  >
                    <option value="">Select Service Type...</option>
                    {serviceTypes.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Service Display Name *</label>
                <input
                  type="text"
                  placeholder="e.g. Premium App Hosting Staging Env"
                  value={serviceForm.name}
                  onChange={e => setServiceForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Price (₹)</label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={serviceForm.price}
                    onChange={e => setServiceForm(prev => ({ ...prev, price: e.target.value }))}
                    className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Billing Cycle</label>
                  <select
                    value={serviceForm.billing_cycle}
                    onChange={e => setServiceForm(prev => ({ ...prev, billing_cycle: e.target.value }))}
                    className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white"
                  >
                    <option value="One-Time">One-Time</option>
                    <option value="Monthly">Monthly</option>
                    <option value="Quarterly">Quarterly</option>
                    <option value="Half-Yearly">Half-Yearly</option>
                    <option value="Annually">Annually</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Status</label>
                  <select
                    value={serviceForm.status}
                    onChange={e => setServiceForm(prev => ({ ...prev, status: e.target.value }))}
                    className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white"
                  >
                    <option value="Active">Active</option>
                    <option value="Suspended">Suspended</option>
                    <option value="Terminated">Terminated</option>
                    <option value="Expired">Expired</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Start Date</label>
                  <input
                    type="date"
                    value={serviceForm.start_date}
                    onChange={e => setServiceForm(prev => ({ ...prev, start_date: e.target.value }))}
                    className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Expiry Date</label>
                  <input
                    type="date"
                    value={serviceForm.expiry_date}
                    onChange={e => setServiceForm(prev => ({ ...prev, expiry_date: e.target.value }))}
                    className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                  />
                </div>
              </div>

              {/* Hosting Configuration Block (Conditional) */}
              {showHostingFields && (
                <div className="bg-violet-50/40 border border-violet-100 rounded-2xl p-5 space-y-4">
                  <h4 className="text-sm font-black text-violet-950 flex items-center gap-1">
                    <FiServer /> Server & Hosting Specifications
                  </h4>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Server Name</label>
                      <input
                        type="text"
                        placeholder="e.g. AWS EC2-Prod"
                        value={serviceForm.server_name}
                        onChange={e => setServiceForm(prev => ({ ...prev, server_name: e.target.value }))}
                        className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-600 uppercase mb-1">IP Address</label>
                      <input
                        type="text"
                        placeholder="e.g. 54.12.89.201"
                        value={serviceForm.ip_address}
                        onChange={e => setServiceForm(prev => ({ ...prev, ip_address: e.target.value }))}
                        className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-gray-600 uppercase mb-1">CPU cores</label>
                      <input
                        type="text"
                        placeholder="e.g. 4 vCPUs"
                        value={serviceForm.cpu}
                        onChange={e => setServiceForm(prev => ({ ...prev, cpu: e.target.value }))}
                        className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-600 uppercase mb-1">RAM Size</label>
                      <div className="relative flex items-center">
                        <input
                          type="text"
                          placeholder="e.g. 16"
                          value={ramSize}
                          onChange={e => setRamSize(e.target.value)}
                          className="w-full border border-slate-200 rounded-xl pl-3 pr-10 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white"
                        />
                        <span className="absolute right-3 text-sm text-gray-400 font-semibold select-none">GB</span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Hard Disk (Storage)</label>
                      <div className="flex gap-2">
                        <div className="relative flex-1 flex items-center">
                          <input
                            type="text"
                            placeholder="e.g. 500"
                            value={hdSize}
                            onChange={e => setHdSize(e.target.value)}
                            className="w-full border border-slate-200 rounded-xl pl-3 pr-10 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white"
                          />
                          <span className="absolute right-3 text-sm text-gray-400 font-semibold select-none">GB</span>
                        </div>
                        <select
                          value={hdType}
                          onChange={e => setHdType(e.target.value)}
                          className="border border-slate-200 rounded-xl px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white min-w-[85px]"
                        >
                          <option value="SSD">SSD</option>
                          <option value="NVMe">NVMe</option>
                          <option value="HDD">HDD</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Backup Provider / Cycle</label>
                      <select
                        value={serviceForm.backup}
                        onChange={e => setServiceForm(prev => ({ ...prev, backup: e.target.value }))}
                        className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white"
                      >
                        <option value="">Select Backup Provider</option>
                        {backupProviders.map(bp => (
                          <option key={bp.id} value={bp.name}>{bp.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Control Panel</label>
                      <select
                        value={serviceForm.panel}
                        onChange={e => setServiceForm(prev => ({ ...prev, panel: e.target.value }))}
                        className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white"
                      >
                        <option value="">Select Control Panel</option>
                        {controlPanels.map(cp => (
                          <option key={cp.id} value={cp.name}>{cp.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Username</label>
                      <input
                        type="text"
                        placeholder="Admin login username"
                        value={serviceForm.username}
                        onChange={e => setServiceForm(prev => ({ ...prev, username: e.target.value }))}
                        className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Password</label>
                      <input
                        type="text"
                        placeholder="Admin login password"
                        value={serviceForm.password}
                        onChange={e => setServiceForm(prev => ({ ...prev, password: e.target.value }))}
                        className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white"
                      />
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Service Description & Formatting</label>
                <div className="bg-white rounded-xl overflow-hidden border border-slate-200">
                  <ReactQuill
                    theme="snow"
                    value={serviceForm.description}
                    onChange={val => setServiceForm(prev => ({ ...prev, description: val }))}
                    placeholder="Enter details of this service instance with rich formatting options..."
                    className="min-h-[150px]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Remarks / Internal Notes</label>
                <textarea
                  placeholder="Add details about database access, secondary URLs, or team guidelines..."
                  value={serviceForm.remarks}
                  onChange={e => setServiceForm(prev => ({ ...prev, remarks: e.target.value }))}
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 h-20"
                />
              </div>

              <div className="pt-4 flex justify-end gap-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsServiceModalOpen(false)}
                  className="px-4 py-2 border-2 border-slate-200 text-slate-700 bg-white hover:bg-slate-50 rounded-xl text-sm font-semibold transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white rounded-xl text-sm font-semibold shadow transition"
                >
                  Save Specification
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Masters Config Modal */}
      {isTypeModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-3xl w-full max-w-xl max-h-[85vh] overflow-hidden flex flex-col shadow-2xl border border-slate-100">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-gradient-to-r from-violet-50/50 to-indigo-50/50">
              <h3 className="text-lg font-black text-slate-800 animate-fade-in">
                Configuration Masters
              </h3>
              <button
                onClick={() => {
                  setIsTypeModalOpen(false);
                  setEditingType(null);
                  setEditingPanel(null);
                  setEditingBackup(null);
                  setTypeForm({ name: '', has_hosting_details: false, hsn_code: '', price: '', description: '' });
                  setPanelForm({ name: '' });
                  setBackupForm({ name: '' });
                }}
                className="text-gray-400 hover:text-gray-600 text-xl font-bold"
              >
                ✕
              </button>
            </div>

            {/* Config Tabs selector */}
            <div className="flex border-b border-slate-100 bg-slate-50/30">
              <button
                type="button"
                onClick={() => setActiveConfigTab('service_types')}
                className={`flex-1 py-3 text-center text-xs font-bold uppercase tracking-wider border-b-2 transition ${
                  activeConfigTab === 'service_types'
                    ? 'border-violet-600 text-violet-700 bg-white'
                    : 'border-transparent text-gray-500 hover:text-violet-600 hover:bg-slate-50'
                }`}
              >
                Service Types
              </button>
              <button
                type="button"
                onClick={() => setActiveConfigTab('control_panels')}
                className={`flex-1 py-3 text-center text-xs font-bold uppercase tracking-wider border-b-2 transition ${
                  activeConfigTab === 'control_panels'
                    ? 'border-violet-600 text-violet-700 bg-white'
                    : 'border-transparent text-gray-500 hover:text-violet-600 hover:bg-slate-50'
                }`}
              >
                Control Panels
              </button>
              <button
                type="button"
                onClick={() => setActiveConfigTab('backup_providers')}
                className={`flex-1 py-3 text-center text-xs font-bold uppercase tracking-wider border-b-2 transition ${
                  activeConfigTab === 'backup_providers'
                    ? 'border-violet-600 text-violet-700 bg-white'
                    : 'border-transparent text-gray-500 hover:text-violet-600 hover:bg-slate-50'
                }`}
              >
                Backup Providers
              </button>
            </div>

            <div className="p-6 overflow-y-auto space-y-6 flex-1">
              {activeConfigTab === 'service_types' && (
                <>
                  {/* Form */}
                  <form onSubmit={handleSaveType} className="bg-slate-50/50 border border-slate-100 rounded-2xl p-4 space-y-4">
                    <h4 className="text-xs font-black text-slate-800 uppercase tracking-widest">
                      {editingType ? 'Edit Master Category' : 'Create Master Category'}
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Service Type Name</label>
                        <input
                          type="text"
                          placeholder="e.g. VPS Hosting"
                          value={typeForm.name}
                          onChange={e => setTypeForm(prev => ({ ...prev, name: e.target.value }))}
                          className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-600 uppercase mb-1">HSN / SAC Code</label>
                        <input
                          type="text"
                          placeholder="e.g. 998311"
                          value={typeForm.hsn_code}
                          onChange={e => setTypeForm(prev => ({ ...prev, hsn_code: e.target.value }))}
                          className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Default Unit Price (₹)</label>
                      <input
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        value={typeForm.price}
                        onChange={e => setTypeForm(prev => ({ ...prev, price: e.target.value }))}
                        className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white"
                      />
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="has_hosting_details"
                        checked={typeForm.has_hosting_details}
                        onChange={e => setTypeForm(prev => ({ ...prev, has_hosting_details: e.target.checked }))}
                        className="h-4 w-4 text-violet-600 border-slate-300 rounded focus:ring-violet-500 cursor-pointer"
                      />
                      <label htmlFor="has_hosting_details" className="ml-2.5 text-xs font-semibold text-slate-700 cursor-pointer select-none">
                        Requires server hosting parameters (RAM/HD/CPU/ID-Pass)
                      </label>
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                      {editingType && (
                        <button
                          type="button"
                          onClick={() => {
                            setEditingType(null);
                            setTypeForm({ name: '', has_hosting_details: false, hsn_code: '', price: '', description: '' });
                          }}
                          className="px-3 py-1.5 border border-slate-200 text-slate-700 bg-white hover:bg-slate-50 rounded-lg text-xs font-semibold transition"
                        >
                          Cancel
                        </button>
                      )}
                      <button
                        type="submit"
                        className="px-4 py-1.5 bg-violet-600 hover:bg-violet-700 text-white rounded-lg text-xs font-semibold shadow transition"
                      >
                        {editingType ? 'Update Master' : 'Add to Masters'}
                      </button>
                    </div>
                  </form>

                  {/* Master Listing */}
                  <div className="space-y-3">
                    <h4 className="text-xs font-black text-slate-500 uppercase tracking-widest">Available Masters</h4>
                    <div className="border border-slate-100 rounded-2xl divide-y divide-slate-100 overflow-hidden bg-white">
                      {serviceTypes.length === 0 ? (
                        <p className="p-4 text-sm text-gray-400 italic text-center">No categories registered.</p>
                      ) : (
                        serviceTypes.map(t => (
                          <div key={t.id} className="p-3.5 flex justify-between items-center hover:bg-slate-50/40 transition">
                            <div>
                              <p className="text-sm font-bold text-slate-800">{t.name}</p>
                              <div className="flex items-center gap-3 mt-1 text-[10px] text-gray-400 font-semibold uppercase">
                                <span>{t.has_hosting_details === 1 ? '💻 Hosting Specs Active' : '📄 Base Service Only'}</span>
                                {t.hsn_code && <span>• HSN: {t.hsn_code}</span>}
                                {t.price !== undefined && <span>• Price: ₹{Number(t.price).toLocaleString('en-IN')}</span>}
                              </div>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <button
                                onClick={() => handleOpenOrderModal(t)}
                                className="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-[10px] rounded-lg shadow-sm transition uppercase tracking-wider shrink-0 flex items-center gap-1 mr-1"
                                title="Order Service"
                              >
                                🛒 Order
                              </button>
                              <button
                                onClick={() => handleEditTypeClick(t)}
                                className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-lg transition"
                                title="Edit Category"
                              >
                                <FiEdit2 size={13} />
                              </button>
                              <button
                                onClick={() => handleDeleteType(t.id)}
                                className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition"
                                title="Delete Category"
                              >
                                <FiTrash2 size={13} />
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </>
              )}

              {activeConfigTab === 'control_panels' && (
                <>
                  {/* Form */}
                  <form onSubmit={handleSavePanel} className="bg-slate-50/50 border border-slate-100 rounded-2xl p-4 space-y-4">
                    <h4 className="text-xs font-black text-slate-800 uppercase tracking-widest">
                      {editingPanel ? 'Edit Control Panel' : 'Add Control Panel'}
                    </h4>
                    <div>
                      <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Control Panel Name</label>
                      <input
                        type="text"
                        placeholder="e.g. cPanel, Plesk, CyberPanel"
                        value={panelForm.name}
                        onChange={e => setPanelForm({ name: e.target.value })}
                        className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white"
                        required
                      />
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                      {editingPanel && (
                        <button
                          type="button"
                          onClick={() => {
                            setEditingPanel(null);
                            setPanelForm({ name: '' });
                          }}
                          className="px-3 py-1.5 border border-slate-200 text-slate-700 bg-white hover:bg-slate-50 rounded-lg text-xs font-semibold transition"
                        >
                          Cancel
                        </button>
                      )}
                      <button
                        type="submit"
                        className="px-4 py-1.5 bg-violet-600 hover:bg-violet-700 text-white rounded-lg text-xs font-semibold shadow transition"
                      >
                        {editingPanel ? 'Update' : 'Add'}
                      </button>
                    </div>
                  </form>

                  {/* Panel Listing */}
                  <div className="space-y-3">
                    <h4 className="text-xs font-black text-slate-500 uppercase tracking-widest">Available Control Panels</h4>
                    <div className="border border-slate-100 rounded-2xl divide-y divide-slate-100 overflow-hidden bg-white">
                      {controlPanels.length === 0 ? (
                        <p className="p-4 text-sm text-gray-400 italic text-center">No control panels registered.</p>
                      ) : (
                        controlPanels.map(p => (
                          <div key={p.id} className="p-3.5 flex justify-between items-center hover:bg-slate-50/40 transition">
                            <p className="text-sm font-bold text-slate-800">{p.name}</p>
                            <div className="flex gap-1.5">
                              <button
                                onClick={() => handleEditPanelClick(p)}
                                className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-lg transition"
                                title="Edit Panel"
                              >
                                <FiEdit2 size={13} />
                              </button>
                              <button
                                onClick={() => handleDeletePanel(p.id)}
                                className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition"
                                title="Delete Panel"
                              >
                                <FiTrash2 size={13} />
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </>
              )}

              {activeConfigTab === 'backup_providers' && (
                <>
                  {/* Form */}
                  <form onSubmit={handleSaveBackup} className="bg-slate-50/50 border border-slate-100 rounded-2xl p-4 space-y-4">
                    <h4 className="text-xs font-black text-slate-800 uppercase tracking-widest">
                      {editingBackup ? 'Edit Backup Provider' : 'Add Backup Provider'}
                    </h4>
                    <div>
                      <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Backup Provider Name</label>
                      <input
                        type="text"
                        placeholder="e.g. AWS S3, Google Drive, OneDrive"
                        value={backupForm.name}
                        onChange={e => setBackupForm({ name: e.target.value })}
                        className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white"
                        required
                      />
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                      {editingBackup && (
                        <button
                          type="button"
                          onClick={() => {
                            setEditingBackup(null);
                            setBackupForm({ name: '' });
                          }}
                          className="px-3 py-1.5 border border-slate-200 text-slate-700 bg-white hover:bg-slate-50 rounded-lg text-xs font-semibold transition"
                        >
                          Cancel
                        </button>
                      )}
                      <button
                        type="submit"
                        className="px-4 py-1.5 bg-violet-600 hover:bg-violet-700 text-white rounded-lg text-xs font-semibold shadow transition"
                      >
                        {editingBackup ? 'Update' : 'Add'}
                      </button>
                    </div>
                  </form>

                  {/* Backup Listing */}
                  <div className="space-y-3">
                    <h4 className="text-xs font-black text-slate-500 uppercase tracking-widest">Available Backup Providers</h4>
                    <div className="border border-slate-100 rounded-2xl divide-y divide-slate-100 overflow-hidden bg-white">
                      {backupProviders.length === 0 ? (
                        <p className="p-4 text-sm text-gray-400 italic text-center">No backup providers registered.</p>
                      ) : (
                        backupProviders.map(b => (
                          <div key={b.id} className="p-3.5 flex justify-between items-center hover:bg-slate-50/40 transition">
                            <p className="text-sm font-bold text-slate-800">{b.name}</p>
                            <div className="flex gap-1.5">
                              <button
                                onClick={() => handleEditBackupClick(b)}
                                className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-lg transition"
                                title="Edit Provider"
                              >
                                <FiEdit2 size={13} />
                              </button>
                              <button
                                onClick={() => handleDeleteBackup(b.id)}
                                className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition"
                                title="Delete Provider"
                              >
                                <FiTrash2 size={13} />
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Order Service & KYC Modal */}
      {isOrderModalOpen && selectedOrderServiceType && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl shadow-xl w-full max-w-2xl p-6 relative border border-slate-100 my-8 animate-in fade-in zoom-in-95 duration-150">
            <div className="flex justify-between items-center border-b pb-3 mb-4">
              <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                🛒 Order Service: <span className="text-violet-600">{selectedOrderServiceType.name}</span>
              </h3>
              <button
                type="button"
                onClick={() => setIsOrderModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 text-xl font-bold"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handlePlaceOrder} className="space-y-4">
              {/* Toggle Customer Type */}
              {!isPublicView && (
                <div className="flex gap-4 p-1 bg-slate-100 rounded-xl mb-2">
                  <button
                    type="button"
                    onClick={() => setUseExistingCustomer(true)}
                    className={`flex-1 py-2 text-xs font-bold rounded-lg transition ${useExistingCustomer ? 'bg-white text-indigo-950 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                  >
                    👤 Existing Customer
                  </button>
                  <button
                    type="button"
                    onClick={() => setUseExistingCustomer(false)}
                    className={`flex-1 py-2 text-xs font-bold rounded-lg transition ${!useExistingCustomer ? 'bg-white text-indigo-950 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                  >
                    ➕ Create New Customer
                  </button>
                </div>
              )}

              {/* Seller Company Selection */}
              {isPublicView && companies.length === 1 ? (
                <input type="hidden" value={orderForm.company_id} />
              ) : (
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Select Seller Company *</label>
                  <select
                    value={orderForm.company_id}
                    onChange={e => setOrderForm(prev => ({ ...prev, company_id: e.target.value }))}
                    className="w-full border-2 border-slate-100 rounded-xl px-4 py-2.5 text-sm focus:border-indigo-300 focus:outline-none font-semibold text-slate-800 bg-white"
                    required
                  >
                    <option value="">Select Company...</option>
                    {companies.map(c => (
                      <option key={c.id} value={c.id}>{c.company_name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Customer Details Form */}
              {useExistingCustomer ? (
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Select Existing Customer *</label>
                  <select
                    value={orderForm.customer_id}
                    onChange={e => setOrderForm(prev => ({ ...prev, customer_id: e.target.value }))}
                    className="w-full border-2 border-slate-100 rounded-xl px-4 py-2.5 text-sm focus:border-indigo-300 focus:outline-none font-semibold text-slate-800 bg-white"
                    required
                  >
                    <option value="">Select Customer...</option>
                    {customers.map(c => (
                      <option key={c.id} value={c.id}>{c.company_name} ({c.email})</option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="space-y-3 p-4 border border-slate-100 rounded-2xl bg-slate-50/50">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Company / Customer Name *</label>
                      <input
                        type="text"
                        value={orderForm.company_name}
                        onChange={e => setOrderForm(prev => ({ ...prev, company_name: e.target.value }))}
                        className="w-full border-2 border-slate-100 rounded-xl px-3 py-2 text-xs focus:border-indigo-300 focus:outline-none text-slate-800 font-semibold"
                        placeholder="e.g. Acme Corp"
                        required={!useExistingCustomer}
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Email Address *</label>
                      <input
                        type="email"
                        value={orderForm.email}
                        onChange={e => setOrderForm(prev => ({ ...prev, email: e.target.value }))}
                        className="w-full border-2 border-slate-100 rounded-xl px-3 py-2 text-xs focus:border-indigo-300 focus:outline-none text-slate-800 font-semibold"
                        placeholder="e.g. billing@acme.com"
                        required={!useExistingCustomer}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">City</label>
                      <input
                        type="text"
                        value={orderForm.city}
                        onChange={e => setOrderForm(prev => ({ ...prev, city: e.target.value }))}
                        className="w-full border-2 border-slate-100 rounded-xl px-3 py-2 text-xs focus:border-indigo-300 focus:outline-none text-slate-800 font-semibold"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">State</label>
                      <input
                        type="text"
                        value={orderForm.state}
                        onChange={e => setOrderForm(prev => ({ ...prev, state: e.target.value }))}
                        className="w-full border-2 border-slate-100 rounded-xl px-3 py-2 text-xs focus:border-indigo-300 focus:outline-none text-slate-800 font-semibold"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Country</label>
                      <input
                        type="text"
                        value={orderForm.country}
                        onChange={e => setOrderForm(prev => ({ ...prev, country: e.target.value }))}
                        className="w-full border-2 border-slate-100 rounded-xl px-3 py-2 text-xs focus:border-indigo-300 focus:outline-none text-slate-800 font-semibold"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">GST Number</label>
                      <input
                        type="text"
                        value={orderForm.gst_number}
                        onChange={e => setOrderForm(prev => ({ ...prev, gst_number: e.target.value }))}
                        className="w-full border-2 border-slate-100 rounded-xl px-3 py-2 text-xs focus:border-indigo-300 focus:outline-none text-slate-800 font-semibold"
                        placeholder="e.g. 07AAAAA1111A1Z1"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Portal Login Password</label>
                      <input
                        type="password"
                        value={orderForm.password}
                        onChange={e => setOrderForm(prev => ({ ...prev, password: e.target.value }))}
                        className="w-full border-2 border-slate-100 rounded-xl px-3 py-2 text-xs focus:border-indigo-300 focus:outline-none text-slate-800 font-semibold"
                        placeholder="Set default password"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Billing Address</label>
                    <textarea
                      rows={2}
                      value={orderForm.address}
                      onChange={e => setOrderForm(prev => ({ ...prev, address: e.target.value }))}
                      className="w-full border-2 border-slate-100 rounded-xl px-3 py-2 text-xs focus:border-indigo-300 focus:outline-none text-slate-800 font-semibold"
                    />
                  </div>
                </div>
              )}

              {/* KYC and PAN */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">PAN Card Number</label>
                  <input
                    type="text"
                    value={orderForm.pancard}
                    onChange={e => setOrderForm(prev => ({ ...prev, pancard: e.target.value }))}
                    className="w-full border-2 border-slate-100 rounded-xl px-4 py-2.5 text-sm focus:border-indigo-300 focus:outline-none text-slate-800 font-semibold"
                    placeholder="e.g. ABCDE1234F"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">KYC Document (PDF/Image)</label>
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    onChange={e => {
                      if (e.target.files && e.target.files[0]) {
                        setKycFile(e.target.files[0]);
                      }
                    }}
                    className="w-full text-xs text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 cursor-pointer"
                  />
                </div>
              </div>

              {/* Order Settings */}
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Billing Cycle *</label>
                <select
                  value={orderForm.billing_cycle}
                  onChange={e => setOrderForm(prev => ({ ...prev, billing_cycle: e.target.value }))}
                  className="w-full border-2 border-slate-100 rounded-xl px-4 py-2.5 text-sm focus:border-indigo-300 focus:outline-none font-semibold text-slate-800 bg-white"
                  required
                >
                  <option value="One-Time">One-Time</option>
                  <option value="Monthly">Monthly</option>
                  <option value="Quarterly">Quarterly</option>
                  <option value="Half-Yearly">Half-Yearly</option>
                  <option value="Yearly">Yearly</option>
                </select>
              </div>

              {/* Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsOrderModalOpen(false)}
                  className="px-5 py-2.5 border rounded-xl text-gray-500 hover:bg-gray-100 font-bold uppercase transition text-xs"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploadingKyc}
                  className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold uppercase transition text-xs shadow-md disabled:opacity-50"
                >
                  {uploadingKyc ? 'Ordering & Uploading...' : 'Place Order'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default Services;
